import { titleTierFor, titleTierEvidence } from '../../lib/titleTierView.js'
import { useState, useEffect, useRef } from 'react'
import { api } from '../../lib/api.js'
import { normalizeSchools } from '../../lib/schools.js'
import { daysToEvent, formatDate, dateQualifier, dateIsCustomerSafe } from '../../lib/championships.js'
import { stubCoach, deriveSeasonStory } from '../../lib/championToClose.js'
import { accountSchool, athletesOf, titleTypeOf, schoolApplies } from '../../lib/championDisplay.js'
import { useCoachContact } from './useCoachContact.js'

/**
 * Assemble a Champion-to-Close dossier for one championship record.
 * Just-in-time enrichment (fires only when a dossier opens, never in bulk):
 *  - owned logo + verified colors via GET /api/hubspot/schools (owned-first).
 *  - shared HubSpot coach/contact lookup, also used by the opportunity drawer.
 *  - season story uses only recorded facts.
 * Read-only — nothing is written back to the championship universe.
 */
function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

export function useDossier(champ) {
  const demo = isDemo()
  const contact = useCoachContact(champ, !demo)
  const [dossier, setDossier] = useState(null)
  const [enrichStatus, setEnrichStatus] = useState('idle') // idle | loading | done | error
  const reqId = useRef(0)

  useEffect(() => {
    if (!champ) { reqId.current++; setDossier(null); return }
    const id = ++reqId.current
    /* The account to enrich on, and the athletes to list under it. One
     * module, shared with the board and every export. */
    const account = accountSchool(champ)
    const athletes = athletesOf(champ)
    const base = {
      championshipId: champ.id,
      factRevision: champ.ctcFactRevision || '',
      championship: {
        ...titleTierFor(champ),
        titleTierEvidence: titleTierEvidence(champ),
        name: champ.name, sport: champ.sport, level: champ.level, conference: champ.conference,
        state: champ.state, season: champ.season,
        /* ⚠️ THE DOSSIER FEEDS OUTBOUND. The ring builder and the email
         * composer both read `effDateLabel`, so whatever is put here can end
         * up in front of a customer. A projected date is a placeholder SR
         * invented — 1,957 records share just 86 of them — and sending one to
         * an athletic director as "your championship is on 2027-01-30" is the
         * single worst thing this codebase could do with it.
         *
         * So a date only reaches this label if it is CONFIRMED. Anything else
         * says so in words, and `effDate` is withheld too, because a consumer
         * that formats the raw Date itself would route straight around the
         * label. The qualified version stays available for the internal panel
         * under a name no outbound path reads. */
        effDate: dateIsCustomerSafe(champ) ? champ.effDate : null,
        effDateLabel: dateIsCustomerSafe(champ) ? formatDate(champ.effDate) : 'No confirmed date',
        // Internal-only: the working date and what kind of date it is.
        workingDate: champ.effDate || null,
        workingDateLabel: champ.effDate
          ? `${formatDate(champ.effDate)}${dateQualifier(champ)}`
          : 'No date',
        dateConfidence: champ.dateConfidence || 'none',
        dateCustomerSafe: dateIsCustomerSafe(champ),
        daysToEvent: daysToEvent(champ),
        estimated: champ.dateEstimated,
        projected: champ.dateProjected,
        // The sourced story travels WITH the championship so every consumer —
        // the panel, the ring builder, the email composer — reads the same
        // facts instead of each deriving its own version.
        story: champ.story || {},
        championSourceUrl: champ.championSourceUrl || '',
        championProvenance: champ.championProvenance || '',
        // The incumbent ring vendor. Carried into the dossier because the
        // displacement email NAMES them — "I know you work with Herff Jones"
        // — and an email that pretends the incumbent does not exist marks the
        // sender as someone who did not do the work. Without this field that
        // variant correctly refuses to compose.
        incumbent: champ.incumbent || '',
      },
      champion: {
        /* ⚠️⚠️ THE WINNING SCHOOL FIRST, AND THE CHAMPION NAME ONLY AS A
         * FALLBACK — IT USED TO BE THE OTHER WAY ROUND.
         *
         * Brian's individual-champion ruling, landed 2026-10-16 as
         * sr-registry/CONTRACT-individual-champion.md: a TEAM title carries the
         * school in BOTH `current_champion` and `sr_winning_school`; an
         * INDIVIDUAL title carries the PERSON in `current_champion` and THEIR
         * SCHOOL in `sr_winning_school`. His reason is exactly this screen — a
         * rep needs the school to find the ACCOUNT and the name for the
         * ENGRAVING, which is why they are two fields.
         *
         * Reading `champion` first therefore hands a PERSON'S NAME to the coach
         * resolver on every individual title. Measured live: FedEx Cup (Tour
         * Championship), champion "Tommy Fleetwood" → school_not_found, after
         * spending a company search to discover that a golfer is not a school.
         * On 156 individual-title records that is the normal case, not an edge.
         *
         * `schoolIsFallback` is carried so the panel can say which field it is
         * working from rather than presenting a guess as a lookup.
         *
         * ⚠️⚠️⚠️ 2026-10-19 — THE FALLBACK IS NOW GONE, NOT JUST REORDERED,
         * AND THE REORDER ALONE WAS NOT ENOUGH.
         *
         * Reordering fixed the common case and left the dangerous one: when
         * `winningSchool` is BLANK — which, measured 2026-10-19 on the live
         * object, is 117 of the 117 in-scope crowned individual titles, i.e.
         * every single one — `champ.champion` was still handed to
         * `api.logoFind({ entityType:'school' })`, `api.schools()` and
         * `api.coach({ school })` below. So the Fleetwood case in the note
         * above was not fixed by the reorder; it was fixed only for records
         * that have a school, and none of them do yet.
         *
         * And the lookup is not read-only: the Marks Library finder SELF-HEALS
         * by writing an owned copy of whatever it finds into HubSpot File
         * Manager. A person's name entering that path can file an asset
         * against the wrong entity — which makes this a write, not a search.
         *
         * Brian, 2026-10-19: "both of them have to be present." So a missing
         * school is now a GAP THE PANEL SHOWS, never a slot the person's name
         * is borrowed into. `schoolIsFallback` is kept and is now always false
         * — left in place so no consumer reading it breaks, and so the field
         * does not quietly change meaning.
         *
         * ⚠️⚠️⚠️ 2026-10-19, SECOND RULING, AND IT RETIRES THE FALLBACK
         * ENTIRELY RATHER THAN REORDERING IT AGAIN.
         *
         * Brian: "The champion of record is the SCHOOL, and the individual
         * athlete and event are attached as the reference." So
         * `current_champion` now holds a school on EVERY kind of title, and
         * this slot reads it directly — there is no second field to fall back
         * to and no order to get wrong. `schoolIsFallback` is kept and is
         * permanently false, so nothing reading it breaks and the field does
         * not quietly change meaning.
         *
         * Why that matters here specifically: this value is handed to
         * `api.logoFind({ entityType:'school' })`, `api.schools()` and
         * `api.coach({ school })` below. The Marks Library finder SELF-HEALS by
         * writing an owned copy of what it finds into HubSpot File Manager, so
         * a person's name reaching it is a WRITE against the wrong entity, not
         * a failed search. `accountSchool()` returns '' rather than a person
         * or a blob, so a bad lookup cannot start. */
        school: account,
        schoolIsFallback: false,
        /* §3b: on Equestrian / Pro the ATHLETE is correctly the champion of
         * record, so there is no school to find and the panel must not draw a
         * gap. 13 live records, measured 2026-10-19. */
        schoolApplicable: schoolApplies(champ) || titleTypeOf(champ) !== 'individual',
        titleType: titleTypeOf(champ),
        /* THE ATHLETES, listed under the school. This is what the outreach
         * names: "Congrats on [athlete]'s [event] title". */
        athletes: athletes.ok ? athletes.lines : null,
        /* ⚠️ NOT an empty list. Athletes recorded-but-unreadable is a data
         * defect; no athletes recorded is a gap. The panel must be able to say
         * which, so the error travels instead of being flattened to []. */
        athletesError: athletes.ok ? null : athletes.error,
        championName: champ.champion || '',
        logoUrl: '', colors: [], mascot: '',
        enrichment: account ? 'record'
          : titleTypeOf(champ) === 'individual' && !schoolApplies(champ) ? 'not_applicable'
            : 'school_missing',
      },
      coach: stubCoach(champ),
      /* The season story is the SCHOOL's season. It had kept the OLD
       * champion-first order after the line above was first fixed, which is how
       * a half-migrated fallback survives a review. */
      seasonStory: deriveSeasonStory(champ, { school: account }),
    }
    setDossier(base)

    /* Enrich on the SCHOOL ONLY. A blank is a gap to show, never a cue to go
     * searching for whatever else happens to be on the record. */
    const school = account
    if (!school) { setEnrichStatus('idle'); return }

    setEnrichStatus('loading')
    const controller = new AbortController()
    ;(async () => {
      try {
        let enriched
        if (demo) {
          enriched = { logoUrl: '', colors: [{ hex: '#0b1f3a', label: 'Navy' }, { hex: '#c8a24a', label: 'Gold' }], mascot: 'Falcons', markSource: 'owned', needsManual: false }
        } else {
          // Owned-first via the Marks Library finder (self-heals ESPN → owned
          // copy in HubSpot File Manager), colors/mascot backfilled from schools;
          // coach just-in-time from the LIVE coaches resolver (/api/hubspot/coach).
          const [find, schoolsRes] = await Promise.all([
            api.logoFind({ name: school, entityType: 'school', sport: champ.sport || '', level: champ.level || '' }, { signal: controller.signal }).catch(() => null),
            api.schools(school, { signal: controller.signal }).catch(() => null),
          ])
          const match = normalizeSchools(schoolsRes).find((s) => s.name?.toLowerCase() === school.toLowerCase()) || normalizeSchools(schoolsRes)[0]
          const colors = []
          if (find?.primary) colors.push({ hex: find.primary, label: 'Primary' })
          if (find?.secondary) colors.push({ hex: find.secondary, label: 'Secondary' })
          enriched = {
            logoUrl: find?.logo || match?.logoUrl || '',
            colors: colors.length ? colors : (match?.colors || []),
            mascot: match?.mascot || '',
            markSource: find?.owned ? 'owned' : find?.source || (match ? 'hubspot' : 'missing'),
            needsManual: !!find?.needsManual,
          }
        }
        if (id !== reqId.current) return
        setDossier((d) => d && {
          ...d,
          champion: {
            ...d.champion, ...(enriched || {}),
            enrichment: enriched && (enriched.logoUrl || enriched.colors?.length) ? 'hubspot' : d.champion.enrichment,
          },
        })
        setEnrichStatus('done')
      } catch (err) {
        if (err?.name === 'AbortError' || id !== reqId.current) return
        setEnrichStatus('error')
      }
    })()
    return () => { reqId.current = id + 1; controller.abort() }
  }, [champ, demo])

  useEffect(() => {
    if (!champ) return
    const response = contact.response
    setDossier(d => d && String(d.championshipId) === String(champ.id) ? {
      ...d,
      // A directory refresh must not discard a recipient explicitly chosen or
      // a coach entered by the rep while the read was in flight.
      coach: champ.ctcFactsVerified || ['manual', 'directory-picked'].includes(d.coach?.source) ? d.coach : contact.coach,
      committee: response?.committee ?? null,
      suggestions: response?.suggestions ?? null,
      reach: response?.reach ?? null,
      peopleCount: response?.peopleCount ?? null,
      schoolCandidates: response?.candidates || null,
      resolvedSchool: response?.school || null,
      coachReview: response?.needs_review || (contact.status === 'error'
        ? { reason: 'contact_lookup_failed', detail: 'Contact lookup unavailable. Needs continued research.' } : null),
    } : d)
    // Contact identity includes school, sport, level, state, division and season.
  }, [champ, contact.key, contact.response, contact.status, contact.coach])

  return { dossier: String(dossier?.championshipId) === String(champ?.id) && (dossier?.factRevision || '') === (champ?.ctcFactRevision || '') ? dossier : null, setDossier, enrichStatus }
}

